import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, message} from 'antd';
import {EditOutlined, FileTextOutlined, PlusOutlined, SendOutlined} from '@/icons';
import {useAppStore} from '@/page/app/context';
import {executePushOnce, getPushHistory, getRoutinePushes} from '@/api/hrPush';
import TaskFormModal from './TaskFormModal';
import HistoryDetailModal from '../components/HistoryDetailModal';
import PushHistoryModal from '../components/PushHistoryModal';
import {ROUTINE_TYPES} from '../routineUtils';
import {formatApiTime} from '../utils';
import AppContent from '@/page/component/AppLayout/AppContent';

export default function RoutinePage() {
    const [{appId}] = useAppStore();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [historyTask, setHistoryTask] = useState(null);
    const [failureHistoryId, setFailureHistoryId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const values = form.getFieldsValue();
            const {data} = await getRoutinePushes(appId, values);
            setRows(data || []);
        } finally {
            setLoading(false);
        }
    }, [appId, form]);

    useEffect(() => {
        if (appId) load();
    }, [appId, load]);

    const executeOnce = row => {
        Modal.confirm({
            title: '确认手动执行一次？',
            content: `将立即按当前配置执行「${row.taskName}」，并生成一条推送日志。`,
            onOk: async () => {
                try {
                    await executePushOnce(appId, row.id);
                    message.success('已触发手动执行');
                    load();
                } catch (e) {
                    // 失败原因（如模板停用）来自后端，必须显式提示，避免点了没反应
                    message.error(e?.message || '执行失败');
                }
            },
        });
    };

    const openFailureDetail = async row => {
        const {data} = await getPushHistory(appId, row.id, {current: 1, size: 1});
        const history = data?.records?.[0];
        if (!history) {
            message.warning('暂无推送日志');
            return;
        }
        setFailureHistoryId(history.id);
    };

    const columns = [
        {
            title: '状态',
            width: 80,
            render: (_, row) => (
                <Tag color={row.enabled ? 'green' : 'blue'}>{row.enabled ? '启用' : '停用'}</Tag>
            ),
        },
        {title: '任务名称', dataIndex: 'taskName'},
        {title: '任务类型', dataIndex: 'taskType', render: value => ROUTINE_TYPES[value] || value},
        {title: '发送时间', dataIndex: 'executeTime', width: 100},
        {title: '执行周期', dataIndex: 'executePeriod', width: 100, render: () => '每日'},
        {
            title: '发送状态',
            width: 100,
            render: (_, row) => {
                if (row.errorMessage) {
                    return (
                        <Tooltip title={row.errorMessage}>
                            <Tag color="red">发送失败</Tag>
                        </Tooltip>
                    );
                }
                if (!row.lastExecuteTime) {
                    return <Tag>未执行</Tag>;
                }
                const hasFailure = row.targetCount > 0 && row.successCount < row.targetCount;

                return hasFailure ? (
                    <Button type="link" danger size="small" onClick={() => openFailureDetail(row)}>
                        ● 失败
                    </Button>
                ) : (
                    <Tag color="green">● 成功</Tag>
                );
            },
        },
        {title: '创建者', dataIndex: 'creatorName', width: 120},
        {
            title: '修改时间',
            dataIndex: 'modifyTime',
            width: 170,
            render: formatApiTime,
        },
        {
            title: '操作',
            width: 190,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(row);
                            setModalOpen(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<SendOutlined />}
                        onClick={() => executeOnce(row)}
                    >
                        手动执行
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => setHistoryTask(row)}
                    >
                        日志
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <AppContent>
            <Card
                title="常规消息推送"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditing(null);
                            setModalOpen(true);
                        }}
                    >
                        新建推送
                    </Button>
                }
            >
                <Form form={form} layout="inline" onFinish={load} style={{marginBottom: 16}}>
                    <Form.Item name="creator">
                        <Input placeholder="创建者" allowClear />
                    </Form.Item>
                    <Form.Item name="taskType">
                        <Select
                            placeholder="任务类型"
                            allowClear
                            style={{width: 160}}
                            options={Object.entries(ROUTINE_TYPES).map(([value, label]) => ({
                                value,
                                label,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="enabled">
                        <Select
                            placeholder="状态"
                            allowClear
                            style={{width: 110}}
                            options={[
                                {value: true, label: '启用'},
                                {value: false, label: '停用'},
                            ]}
                        />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                            搜索
                        </Button>
                        <Button
                            onClick={() => {
                                form.resetFields();
                                load();
                            }}
                        >
                            重置
                        </Button>
                    </Space>
                </Form>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    pagination={{pageSize: 10, showTotal: total => `共 ${total} 条记录`}}
                />
                <TaskFormModal
                    open={modalOpen}
                    appId={appId}
                    rows={rows}
                    editing={editing}
                    onClose={() => setModalOpen(false)}
                    onSaved={() => {
                        setModalOpen(false);
                        load();
                        message.success('保存成功');
                    }}
                />
                {historyTask && (
                    <PushHistoryModal
                        appId={appId}
                        task={historyTask}
                        onClose={() => setHistoryTask(null)}
                    />
                )}
                {failureHistoryId && (
                    <HistoryDetailModal
                        appId={appId}
                        historyId={failureHistoryId}
                        taskName="常规推送"
                        onClose={() => setFailureHistoryId(null)}
                    />
                )}
            </Card>
        </AppContent>
    );
}
